import React from 'react'
import CardCarouselEditor from './CardCarouselEditor'
import CarouselPreviewPane from './CarouselPreviewPane'

/** A LinkRef as stored in metadata: a page reference or an external URL,
 * never both (tc.widget.card_carousel.v1 enforces the oneOf). */
export type MetadataLinkRef = Record<string, string>

export type PinType = 'match' | 'vod' | 'custom'

/** A hand-placed card. Content pins (match/vod) name an id ACE hydrates
 * through the same card builders as dynamic results; custom pins carry their
 * own artwork and link and reference no entity at all. */
export interface CarouselPin {
  id: string
  position: number
  type: PinType
  match_id?: string
  asset_id?: string
  title?: string
  subtitle?: string
  image_url?: string
  alt_text?: string
  link?: MetadataLinkRef
}

export type ShowMoreMode = 'never' | 'always' | 'if_more'

/** link is an optional override; absent means the widget's own collection
 * page, which ACE substitutes and resolves through the same LinkRef path. */
export interface CarouselShowMore {
  mode: ShowMoreMode
  label?: string
  link?: MetadataLinkRef
}

// Config shared by all card-carousel widget types. The card size/orientation
// variant is the widget_type itself (migration 85), not a field here — it's
// chosen via the "Size & orientation" selector below, which changes widget_type.
//
// show_more, pins, filter_groups and live_first are authored here ahead of the
// ACE side that reads them, the way card_fields and ended_grace_minutes already
// are — the API schema accepts them, so configs round-trip today and light up
// when ACE lands.
export interface CarouselMetadata {
  title?: string
  sources?: string[]
  /** Legacy one-group shorthand. Read for configs authored before
   * filter_groups; this editor never writes it. */
  filters?: Record<string, unknown>
  filter_groups?: Array<Record<string, unknown>>
  sort?: string
  live_first?: boolean
  limit?: number
  ended_grace_minutes?: number
  card_fields?: string[]
  show_more?: CarouselShowMore
  pins?: CarouselPin[]
  [key: string]: unknown
}

// Size/orientation is the widget_type rather than a metadata field, so it is
// authored in WidgetForm's Basics section alongside name and title — this
// editor owns only the config.
interface CarouselEditorProps {
  metadata: CarouselMetadata
  onChange: (metadata: CarouselMetadata) => void
  widgetId?: string
  /** The carousel variant being authored — it decides the card crop. */
  widgetType: string
}

const CarouselEditor: React.FC<CarouselEditorProps> = ({ metadata, onChange, widgetId, widgetType }) => {
  return (
    <>
      <CardCarouselEditor
        metadata={metadata}
        onChange={onChange}
        widgetId={widgetId}
        widgetType={widgetType}
      />
      {/* The preview is an editing surface, not a read-out: pins are placed by
          dragging their tile in the resolved rail. */}
      <CarouselPreviewPane metadata={metadata} onChange={onChange} />
    </>
  )
}

export default CarouselEditor
